// src/components/TagStoryList.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import TagSEO from './TagSEO';
import { StarRating } from 'react-flexible-star-rating';
import { formatDate } from '../utils/dateFormat';
import { useTypograf } from '../hooks/useTypograf';
import { getPageItems } from '../utils/pagination';

const getRatingWord = (count) => {
  if (count % 10 === 1 && count % 100 !== 11) return 'оценка';
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'оценки';
  return 'оценок';
};

const TagStoryList = () => {
  const { tagSlug } = useParams();
  const navigate   = useNavigate();
  const { search } = useLocation();
  const processText = useTypograf();

  // Функция для парсинга ?page= из URL
  const getPageFromSearch = () => {
    const params = new URLSearchParams(search);
    const p = parseInt(params.get('page'), 10);
    return isNaN(p) || p < 1 ? 1 : p;
  };

  const [currentPage, setCurrentPage] = useState(getPageFromSearch());
  const [stories,      setStories]      = useState([]);
  const [tagInfo,      setTagInfo]      = useState(null);
  const [totalPages,   setTotalPages]   = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  // При изменении search (назад/вперёд) синхронизируем currentPage
  useEffect(() => {
    const pageInUrl = getPageFromSearch();
    if (pageInUrl !== currentPage) {
      setCurrentPage(pageInUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Фетчим данные (тег + статьи) при tagSlug или currentPage
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1) Инфо по тегу
        const tagResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/tags/${tagSlug}/`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${process.env.REACT_APP_API_TOKEN}` },
          }
        );
        setTagInfo(tagResponse.data);

        // 2) Сами статьи
        const storiesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/stories/?tag=${tagSlug}&page=${currentPage}&ordering=-pub_date`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${process.env.REACT_APP_API_TOKEN}` },
          }
        );

        setStories(storiesResponse.data.results);
        setTotalPages(Math.ceil(storiesResponse.data.count / 10));
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.detail || 'Ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tagSlug, currentPage]);

  if (loading) return <div>Загрузка...</div>;
  if (error)   return <div className="error-message">{error}</div>;
  if (!tagInfo) return <div>Тег не найден</div>;

  return (
    <div>
      <TagSEO tag={tagInfo} />
      <h1>Истории с тегом: #{tagInfo.name}</h1>

      <div className="stories-grid">
        {stories.map(story => (
          <article key={story.id} className="story-card">
            <h2>{processText(story.title)}</h2>
            <time dateTime={story.pub_date}>{formatDate(story.pub_date)}</time>
            {story.average_rating > 0 && (
              <div className="story-list-rating">
                <StarRating
                  starsLength={5}
                  isHalfRatingEnabled={false}
                  isReadOnly
                  initialRating={story.average_rating}
                  dimension={15}
                  color="#FFD700"
                />
                <span>({story.ratings_count} {getRatingWord(story.ratings_count)})</span>
              </div>
            )}
            {story.image && (
              <picture>
                <source srcSet={story.image} type="image/webp" />
                <source srcSet={story.image_jpg} type="image/jpeg" />
                <img
                  src={story.image_jpg}
                  alt={story.title}
                  className="story-thumbnail"
                  loading="lazy"
                  onError={e => { e.target.onerror = null; e.target.src = '/media/fallback-image.jpg'; }}
                />
              </picture>
            )}
            <div className="story-content">
              {processText(story.content?.substring(0, 200) + '...')
                .split('\n\n')
                .map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}
            </div>
            <Link to={`/stories/${story.slug}`} className="read-more-link">
              Читать далее →
            </Link>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          {getPageItems(currentPage, totalPages).map((item, idx) =>
            typeof item === 'number' ? (
              <button
                key={item}
                onClick={() => {
                  setCurrentPage(item);
                  navigate(`?page=${item}`);
                }}
                className={currentPage === item ? 'active' : ''}
              >
                {item}
              </button>
            ) : (
              <span key={idx} className="pagination-ellipsis">…</span>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default TagStoryList;